import { useMemo } from 'react';

import { ChevronDownIcon } from '@braskai/icons';
import { Chip, Typography } from '@braskai/ui-kit';

import { Box, Stack } from '@mui/material';

import { prettifyPlanName } from '@/widgets/pricing/pricingCards/lib/utils/getPlanName';

import { useBalanceStore } from '@/entities/balance';
import { useCurrentUser } from '@/entities/user/currentUser';

import { useInterfaceLanguage } from '@/shared/i18n';
import { Plan, PlanCategory } from '@/shared/types/Billing';

import { PRICING_CARD_FEATURES } from '../../constants/pricingCardFeatures';
import { SContainer, STitleContainer } from './styled';

type CardHeaderProps = {
  isExpanded: boolean;
  isMobile: boolean;
  isPriceActive: boolean;
  plan: Plan;
  onToggleExpand: () => void;
};

export const CardHeader = ({ isExpanded, isMobile, isPriceActive, plan, onToggleExpand }: CardHeaderProps) => {
  const { data: user } = useCurrentUser();
  const { t } = useInterfaceLanguage();
  const { hasCanceledSubscription, hasExpiredSubscription, hasTrialingEnterprisePlan } = useBalanceStore();

  const isEnterprise = plan.category === PlanCategory.Enterprise;
  const { name } = plan;
  const { descriptionKey } = PRICING_CARD_FEATURES[plan.category];

  const planLabel = useMemo(() => {
    const showCurrentLabel =
      user?.subscription &&
      !hasCanceledSubscription &&
      !hasExpiredSubscription &&
      !hasTrialingEnterprisePlan &&
      isPriceActive;

    if (showCurrentLabel) {
      return <Chip label={t('pricing.plan.currentShort')} color="success" />;
    }

    return null;
  }, [
    hasCanceledSubscription,
    hasExpiredSubscription,
    hasTrialingEnterprisePlan,
    isPriceActive,
    t,
    user?.subscription,
  ]);

  return (
    <SContainer>
      <STitleContainer>
        <Typography color={isEnterprise ? 'common.white' : 'grey.900'} variant="title-4-sb">
          {prettifyPlanName(name)}
        </Typography>
        {planLabel}
      </STitleContainer>
      <Stack alignItems="center" direction="row" gap={4}>
        <Typography color={isEnterprise ? 'grey.300' : 'grey.500'} variant="body-2-md">
          {descriptionKey ? t(descriptionKey) : null}
        </Typography>
        {isMobile && (
          <Box
            sx={(theme) => ({
              '&:focus-visible': { outline: `2px solid ${theme.palette.primary[600]}`, outlineOffset: 2 },
              color: isEnterprise ? 'common.white' : 'grey.900',
              cursor: 'pointer',
              display: 'flex',
              flexShrink: 0,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transformOrigin: 'center',
            })}
            aria-expanded={isExpanded}
            aria-label={t('pricing.plan.toggleDetails')}
            data-testid="card-header-toggle-description"
            role="button"
            tabIndex={0}
            onClick={onToggleExpand}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onToggleExpand();
              }
            }}
          >
            <ChevronDownIcon height={20} width={20} />
          </Box>
        )}
      </Stack>
    </SContainer>
  );
};
